/**
 * Central fetch wrapper for the Canary CLI.
 *
 * Exists to classify failures so corporate-proxy interference (Zscaler,
 * Netskope, Cloudflare Access, etc.) is distinguishable from real auth
 * expiry. Every non-2xx or transport error gets a specific, actionable
 * message instead of the historical blanket "session may have expired."
 *
 * Set CANARY_DEBUG=1 (or pass --debug to the CLI) to log every request
 * and its classification — this is what affected users should share
 * with us or their IT/Zscaler admin.
 */

import process from 'node:process';
import { debug, isDebug } from './debug.js';
import { decodeJwtPayload } from './jwt.js';

/** Header every canary API response carries. Absence => response did not come from our API. */
const CANARY_REQUEST_ID_HEADER = 'x-canary-request-id';

/** Default per-request timeout. Zscaler-half-opened sockets have been observed
 * hanging indefinitely; the CLI should give up and classify rather than hang. */
const DEFAULT_TIMEOUT_MS = 30_000;

/** Treat a JWT exp within this grace window as still valid — tolerates the
 * typical handful of seconds of client-clock skew so we don't mislabel a
 * 30-second-fast clock as `token-revoked`. */
const JWT_EXP_SKEW_SEC = 60;

/**
 * Best-effort scrub of `Bearer <token>` and bare three-segment JWTs from
 * error text before we print it. Doctor output is explicitly coached to be
 * shared with IT — if a proxy block page reflects the Authorization header,
 * we do not want the token in that paste.
 */
function redactSecrets(text: string): string {
  return text
    .replace(/Bearer\s+[A-Za-z0-9._-]+/gi, 'Bearer [redacted]')
    .replace(/\beyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\b/g, '[redacted-jwt]');
}

/** Strip query string — query params sometimes carry one-time tokens. */
function redactUrl(url: string): string {
  const q = url.indexOf('?');
  return q === -1 ? url : `${url.slice(0, q)}?[redacted]`;
}

export type FailureKind =
  | 'tls'
  | 'network'
  | 'html-body'
  | 'proxy-injected-4xx'
  | 'token-revoked'
  | 'token-expired'
  | 'unauthorized'
  | 'http-error';

export interface ClassifiedFailure {
  kind: FailureKind;
  status: number | null;
  url: string;
  message: string;
  /**
   * Response body text. Full body for `http-error` (so callers can parse
   * structured error payloads); truncated snippet for proxy/TLS failures
   * where the body is likely an HTML block page.
   */
  body?: string;
  /** Best-effort content-type. */
  contentType?: string;
  /** Raw error object for transport failures. */
  cause?: unknown;
  /**
   * Value of `x-canary-request-id` on the response, if any. Present means
   * the response came from our API (even for `token-revoked` / `token-expired`
   * / `unauthorized`); absent means the response was injected or the request
   * never reached us.
   */
  requestId?: string | null;
}

/**
 * Node/undici TLS error codes that indicate a MITM proxy (Zscaler et al.)
 * is intercepting HTTPS without the user trusting the proxy's root CA.
 */
const TLS_CODES = new Set([
  'UNABLE_TO_VERIFY_LEAF_SIGNATURE',
  'UNABLE_TO_GET_ISSUER_CERT',
  'UNABLE_TO_GET_ISSUER_CERT_LOCALLY',
  'SELF_SIGNED_CERT_IN_CHAIN',
  'DEPTH_ZERO_SELF_SIGNED_CERT',
  'CERT_HAS_EXPIRED',
  'CERT_UNTRUSTED',
  'ERR_TLS_CERT_ALTNAME_INVALID',
  'CERT_SIGNATURE_FAILURE',
]);

function extractErrorCode(err: unknown): string | null {
  if (!err || typeof err !== 'object') return null;
  const withCode = err as { code?: unknown; cause?: unknown };
  if (typeof withCode.code === 'string') return withCode.code;
  if (withCode.cause) return extractErrorCode(withCode.cause);
  return null;
}

function looksLikeHtml(body: string, contentType: string | null): boolean {
  if (contentType && contentType.toLowerCase().includes('text/html')) return true;
  const trimmed = body.trimStart();
  return trimmed.startsWith('<!') || trimmed.startsWith('<html') || trimmed.startsWith('<HTML');
}

/** Decode JWT `exp` and compare to now (with a small skew grace); null if undecodable. */
function jwtExpStatus(token: string | undefined): 'expired' | 'valid' | 'unknown' {
  if (!token) return 'unknown';
  const payload = decodeJwtPayload(token);
  const exp = payload && typeof payload.exp === 'number' ? (payload.exp as number) : null;
  if (exp === null) return 'unknown';
  const nowSec = Math.floor(Date.now() / 1000);
  return exp < nowSec - JWT_EXP_SKEW_SEC ? 'expired' : 'valid';
}

/** Warn once per process when sending Authorization to a non-local http:// URL. */
let warnedAboutPlaintext = false;
function maybeWarnPlaintextAuth(url: string, hasAuthHeader: boolean): void {
  if (!hasAuthHeader || warnedAboutPlaintext) return;
  if (!url.startsWith('http://')) return;
  try {
    const host = new URL(url).hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return;
  } catch {
    return;
  }
  warnedAboutPlaintext = true;
  process.stderr.write(
    `[canary] WARNING: sending Authorization over plaintext http:// (${url}). ` +
      'Your API token may be exposed to the network. Use https:// in CANARY_API_URL.\n'
  );
}

function hasAuthHeader(init: RequestInit): boolean {
  const h = init.headers;
  if (!h) return false;
  if (h instanceof Headers) return h.has('authorization');
  if (Array.isArray(h)) return h.some(([k]) => k.toLowerCase() === 'authorization');
  return Object.keys(h).some((k) => k.toLowerCase() === 'authorization');
}

/**
 * Wrap global `fetch` with classification. Always resolves — never throws —
 * so callers can make a single uniform decision from the returned shape.
 */
export async function apiFetch(
  url: string,
  init: RequestInit & { token?: string; timeoutMs?: number } = {}
): Promise<
  | { ok: true; response: Response; requestId: string | null }
  | { ok: false; failure: ClassifiedFailure }
> {
  const { token, timeoutMs, ...fetchInit } = init;
  maybeWarnPlaintextAuth(url, hasAuthHeader(fetchInit));
  debug(`→ ${fetchInit.method ?? 'GET'} ${redactUrl(url)}`);

  // Merge caller-supplied signal with our timeout so both can abort.
  // timeoutMs <= 0 disables the timeout (use for long-lived SSE streams).
  const timeout = timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timeoutSignal = timeout > 0 ? AbortSignal.timeout(timeout) : null;
  const effectiveSignal =
    fetchInit.signal && timeoutSignal
      ? AbortSignal.any([fetchInit.signal, timeoutSignal])
      : (fetchInit.signal ?? timeoutSignal ?? undefined);

  let response: Response;
  try {
    response = await fetch(url, { ...fetchInit, signal: effectiveSignal });
  } catch (err) {
    const isTimeout =
      (err as { name?: string })?.name === 'TimeoutError' ||
      Boolean(timeoutSignal?.aborted);
    if (isTimeout) {
      const msg = `Request to ${redactUrl(url)} timed out after ${timeout}ms.`;
      debug(`✖ network timeout`);
      return {
        ok: false,
        failure: { kind: 'network', status: null, url, message: msg, cause: err },
      };
    }
    const code = extractErrorCode(err);
    if (code && TLS_CODES.has(code)) {
      const msg = [
        `TLS verification failed talking to ${redactUrl(url)} (${code}).`,
        'A corporate proxy (Zscaler, Netskope, ZPA, Cloudflare Access) is likely intercepting HTTPS.',
        'Fixes: point Node at the proxy\'s root CA with',
        '    NODE_EXTRA_CA_CERTS=/path/to/corporate-root.pem canary …',
        'or on Node 20+ enable the system trust store with',
        '    NODE_OPTIONS=--use-system-ca canary …',
      ].join('\n');
      debug(`✖ tls ${code}`);
      return { ok: false, failure: { kind: 'tls', status: null, url, message: msg, cause: err } };
    }
    const msg = `Network error reaching ${redactUrl(url)}: ${String((err as Error).message ?? err)}`;
    debug(`✖ network ${msg}`);
    return { ok: false, failure: { kind: 'network', status: null, url, message: msg, cause: err } };
  }

  const requestId = response.headers.get(CANARY_REQUEST_ID_HEADER);
  const contentType = response.headers.get('content-type');
  debug(`← ${response.status} ${redactUrl(url)} (ct=${contentType ?? 'n/a'} rid=${requestId ?? 'none'})`);

  if (response.ok) {
    return { ok: true, response, requestId };
  }

  // Read body once so we can classify and still return something useful.
  // Scrub secrets before constructing user-facing `snippet`; `bodyText` is
  // kept intact for callers that parse structured JSON error bodies.
  const bodyText = await response.clone().text().catch(() => '');
  const snippet = redactSecrets(bodyText.slice(0, 200));

  // HTML on a JSON endpoint => proxy block page or captive portal.
  if (looksLikeHtml(bodyText, contentType)) {
    return {
      ok: false,
      failure: {
        kind: 'html-body',
        status: response.status,
        url,
        contentType: contentType ?? undefined,
        requestId,
        body: snippet,
        message: [
          `Got an HTML response from ${url} (status ${response.status}).`,
          'A corporate proxy block page or captive portal is intercepting this request — the',
          'response did not come from the Canary API.',
          'Check that your proxy (e.g. Zscaler) allowlists the API hostname, and that any',
          'required sign-in to the proxy itself is current.',
          snippet ? `First bytes: ${snippet.replace(/\s+/g, ' ')}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      },
    };
  }

  // 401/403 without our header => not our response.
  if ((response.status === 401 || response.status === 403) && !requestId) {
    return {
      ok: false,
      failure: {
        kind: 'proxy-injected-4xx',
        status: response.status,
        url,
        contentType: contentType ?? undefined,
        requestId,
        body: snippet,
        message: [
          `Got ${response.status} from ${url} but the response is missing the \`${CANARY_REQUEST_ID_HEADER}\` header.`,
          'This response was almost certainly injected by a corporate proxy (Zscaler, Netskope,',
          'Cloudflare Access) rather than returned by our API — retrying "canary login" will not fix it.',
          'Ask your IT team to allowlist the API host, or re-auth to the proxy and try again.',
        ].join('\n'),
      },
    };
  }

  if (response.status === 401) {
    const jwtStatus = jwtExpStatus(token);
    if (jwtStatus === 'valid') {
      return {
        ok: false,
        failure: {
          kind: 'token-revoked',
          status: 401,
          url,
          requestId,
          body: snippet,
          message: [
            'Server rejected a token that is still valid locally (unexpired JWT).',
            'Likely causes: the token was revoked, the server\'s signing key rotated, or the',
            'user/org was deactivated. `canary login` will issue a fresh token.',
          ].join('\n'),
        },
      };
    }
    if (jwtStatus === 'expired') {
      return {
        ok: false,
        failure: {
          kind: 'token-expired',
          status: 401,
          url,
          requestId,
          body: snippet,
          message: 'Your stored token has expired. Run: canary login',
        },
      };
    }
    return {
      ok: false,
      failure: {
        kind: 'unauthorized',
        status: 401,
        url,
        requestId,
        body: snippet,
        message: 'Unauthorized. Run: canary login',
      },
    };
  }

  return {
    ok: false,
    failure: {
      kind: 'http-error',
      status: response.status,
      url,
      contentType: contentType ?? undefined,
      requestId,
      body: bodyText,
      message: `${response.status} ${response.statusText}${snippet ? `: ${snippet}` : ''}`,
    },
  };
}

/**
 * Kinds of failure the user can't recover from by retrying the same call —
 * no token on disk, proxy interception, TLS mismatch, rejected JWT. Callers
 * should `exitOnFailure` immediately for these. `http-error` is excluded
 * because the caller often needs to pretty-print a structured error body.
 */
export function isTerminalFailure(failure: ClassifiedFailure): boolean {
  return failure.kind !== 'http-error';
}

/**
 * Print a classified failure and exit(1). Use from command handlers that
 * have no way to recover — matches the historical behavior of the various
 * `if (res.status === 401) { console.error(...); process.exit(1); }` blocks.
 */
export function exitOnFailure(failure: ClassifiedFailure): never {
  process.stderr.write(`Error: ${failure.message}\n`);
  if (isDebug() && failure.body && failure.kind !== 'html-body') {
    process.stderr.write(`[canary-debug] body: ${failure.body}\n`);
  }
  process.exit(1);
}

/**
 * Drop-in for `fetch()` in CLI call sites that parse the body themselves:
 * exits the process for terminal failures (TLS/proxy/unauthorized) so the
 * user sees a diagnostic, and returns a Response otherwise — whether 2xx
 * or an HTTP error (so the caller can still inspect .status / .json()).
 */
export async function fetchOrExit(
  url: string,
  init: RequestInit & { token?: string; timeoutMs?: number } = {}
): Promise<Response> {
  const result = await apiFetch(url, init);
  if (result.ok) return result.response;
  if (isTerminalFailure(result.failure)) exitOnFailure(result.failure);
  // http-error: reconstruct a Response the caller can json() / read status on.
  return new Response(result.failure.body ?? '', {
    status: result.failure.status ?? 500,
    headers: result.failure.contentType
      ? { 'content-type': result.failure.contentType }
      : undefined,
  });
}
